import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { styled } from '@mui/system';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import { ErrorBoundary } from 'react-error-boundary';
import { List } from 'immutable';

import TableView from '../../../../components/TableView/TableView';
import TicketDeliveryDetails from '../../../../components/TicketDeliveryDetails/TicketDeliveryDetails';
import ValueDocumentCoupons from '../ValueDocumentCoupons/ValueDocumentCoupons';
import { gaEvent } from '../../../../utils/googleAnalytics';
import { orderStatuses } from '../../../../constants/constants';
import { STK_TICKET_DELIVERY, MVD_TICKET_DELIVERY } from '../../constants';
import messages from './messages';
import orderDetailsMessages from '../OrderDetails/messages';
import ticketIcon from '../../../../images/ticket.svg';
import styles from './styles';
import ErrorFallBack from '../ErrorFallBack';
import STicketActions from './STicketActions/STicketActions';
import { resetMobileTicketDeviceIdApi } from '../../../apiBooking';
import {
	MVD_TICKET_STATUS,
	MVD_API_RESULT_STATUS,
} from '../ValueDocumentCoupons/constants';

const StyledLink = styled('a')({
	...styles.linkTitle,
});

const WrapperDiv = styled('div')({
	...styles.wrapper,
});

const ButtonsGrid = styled(Grid)({
	marginLeft: 'unset !important',
	marginTop: 'unset !important',
});

const TitleH2 = styled('h2')({
	...styles.title,
});

const SpinnerDiv = styled('div')({
	...styles.spinner,
});

const FulfillmentInformation = ({
	currTDO,
	handleOpenStationInfoDialog,
	order,
	disableAllowAnyCardCollectButton,
	showAmendCTR,
	onAmendCTR,
	bookingItem,
	tdoButtonLabel,
	amendCTRText,
	isProcessingAction,
	sTicketStatus,
	handleSTicketDialogClose,
	returnCoupons,
	authorizeRefund,
	handleOpenReturnCouponsDialog,
	handleOpenAuthorizeRefundDialog,
	handleOpenResendTicketDialog,
	handleOpenTicketDelivery,
	handleAllowAnyCardCollect,
	handleSTicketAction,
	customerNumber,
	onMvdActionSuccess,
}) => {
	const {
		BookingOrderID: orderId,
		BookingResponseValueDocumentUrl,
		BookingOrderResendTicketAllowed,
	} = order;
	const {
		BookingOrderPickUpStation: pickUpStation,
		BookingOrderPickUpStationName: pickUpStationName,
		BookingFulfillmentPaperEmail: paperEmail,
		BookingFulfillmentPhoneCountryCode: smsPhoneCode,
		BookingFulfillmentPhoneNumber: smsPhoneNumber,
	} = order.BookingOrderTDOData.BookingFulfillmentInfo;
	const [isTicketingDetailsExpanded, setIsTicketingDetailsExpanded] =
		useState(false);
	const [mvdSelectedCoupons, setMvdSelectedCoupons] = useState([]);
	const [mvdTicketStatus, setMvdTicketStatus] = useState(
		MVD_TICKET_STATUS.IDLE,
	);
	const [mvdFailedTickets, setMvdFailedTickets] = useState([]);
	const displayResendTicket =
		BookingOrderResendTicketAllowed && BookingResponseValueDocumentUrl;

	const handleExpandTicketingDetails = () => {
		gaEvent(
			isTicketingDetailsExpanded
				? 'collapseTDOAdditionalDetails'
				: 'expandTDOAdditionalDetails',
		);
		setIsTicketingDetailsExpanded(!isTicketingDetailsExpanded);
	};

	const handleMvdCouponSelect = (couponNumber, checked) => {
		setMvdSelectedCoupons((prev) => {
			if (checked) {
				return [...prev, couponNumber];
			}
			return prev.filter((id) => id !== couponNumber);
		});
	};

	const handleMvdTicketAction = () => {
		setMvdTicketStatus(MVD_TICKET_STATUS.LOADING);

		// Map selected coupon numbers to their unique ticket numbers
		const selectedUniqueTicketNumbers = mvdSelectedCoupons
			.map((couponNumber) => {
				const coupon = order.BookingOrderCoupons.find(
					(c) => c.couponNumber === couponNumber,
				);
				return coupon?.BookingCouponsUniqueTicketNumber?.trim();
			})
			.filter(Boolean);

		const apiData = {
			booking: bookingItem,
			selectedCoupons: selectedUniqueTicketNumbers,
			srtCustomerNumber: customerNumber,
		};

		resetMobileTicketDeviceIdApi(
			apiData,
			() => {
				setMvdTicketStatus(MVD_TICKET_STATUS.ERROR);
			},
			(successResponse) => {
				const results = successResponse?.successResponse?.data?.results || [];
				const failed = results
					.filter((r) => r.status === MVD_API_RESULT_STATUS.FAILURE)
					.map((r) => r.srtMobileUniqueTicketNumber);

				if (failed.length > 0 && failed.length < results.length) {
					setMvdFailedTickets(failed);
					setMvdTicketStatus(MVD_TICKET_STATUS.PARTIAL);
				} else if (failed.length === results.length) {
					setMvdTicketStatus(MVD_TICKET_STATUS.ERROR);
				} else {
					setMvdTicketStatus(MVD_TICKET_STATUS.SUCCESS);
				}
			},
		);
	};

	const handleMvdTicketDialogClose = () => {
		if (
			mvdTicketStatus === MVD_TICKET_STATUS.SUCCESS ||
			mvdTicketStatus === MVD_TICKET_STATUS.PARTIAL
		) {
			onMvdActionSuccess(bookingItem);
		}
		setMvdFailedTickets([]);
		setMvdTicketStatus(MVD_TICKET_STATUS.IDLE);
	};

	const tdo = (
		<>
			<div key="description">
				{currTDO.Description}
				{currTDO.MarketingCarrierReferences && (
					<IconButton
						key="TicketingDetailsExpand"
						onClick={handleExpandTicketingDetails}
					>
						{isTicketingDetailsExpanded ? <ExpandLess /> : <ExpandMore />}
					</IconButton>
				)}
			</div>
			{smsPhoneCode && (
				<div key="smsPhone">
					<FormattedMessage
						{...messages.lblSMSPhone}
						values={{ smsPhone: `${smsPhoneCode} ${smsPhoneNumber}` }}
					/>
				</div>
			)}
			{paperEmail && (
				<div key="paperEmail">
					<FormattedMessage
						{...messages.lblPaperEmail}
						values={{ email: paperEmail }}
					/>
				</div>
			)}
			{pickUpStationName && (
				<div key="station">
					<b>
						<FormattedMessage {...messages.lblPickUpStation} />
					</b>{' '}
					<StyledLink
						onClick={() => {
							handleOpenStationInfoDialog(pickUpStation);
						}}
					>
						{pickUpStationName}
					</StyledLink>
				</div>
			)}
			{order.BookingOrderShippingName && (
				<div key="shippingName">
					<FormattedMessage
						{...messages.lblShippingName}
						values={{ name: order.BookingOrderShippingName }}
					/>
				</div>
			)}
			{order.BookingOrderShippingAddress && (
				<div key="shippingAddress">
					<FormattedMessage
						{...messages.lblShippingAddress}
						values={{
							address: order.BookingOrderShippingAddress.address1,
							city: order.BookingOrderShippingAddress.city,
							country: order.BookingOrderShippingAddress.country,
							zipCode: order.BookingOrderShippingAddress.zipCode,
						}}
					/>
				</div>
			)}
		</>
	);
	const data = [
		[
			{
				id: `ticketDeliveryOption_${order.BookingOrderID}`,
				value: tdo,
			},
			{
				id: `ticketLocatorID_${order.BookingOrderID}`,
				value: order.BookingValueDocLocator,
			},
			{
				id: `ticketLocatorStatus_${order.BookingOrderID}`,
				value:
					order.BookingValueDocStatus &&
					order.BookingValueDocStatus.toLowerCase(),
			},
			{
				id: `ticketIssuedDate_${order.BookingOrderID}`,
				value:
					order.BookingResponseValueDocumentIssuedDate &&
					order.BookingResponseValueDocumentIssuedDate,
			},
			{
				id: `retrievalInformation_${order.BookingOrderID}`,
				value: (
					<>
						<div key="customer">
							<FormattedMessage
								{...messages.lblRetrievalInformationCustomer}
								values={{ value: order.BookingOrderRetrievalCustomer }}
							/>
						</div>
						<div key="creditCard">
							<FormattedMessage
								{...messages.lblRetrievalInformationCreditCard}
								values={{ value: order.BookingOrderRetrievalCreditCard }}
							/>
						</div>
						<div key="code">
							<FormattedMessage
								{...messages.lblRetrievalInformationCode}
								values={{ value: order.BookingOrderRetrievalCode }}
							/>
						</div>
					</>
				),
			},
		],
	];

	const tableHeaders = [
		<FormattedMessage {...messages.lblTicketDeliveryOption} />,
		<FormattedMessage {...messages.lblTicketLocator} />,
		<FormattedMessage {...messages.lblTicketLocatorStatus} />,
		<FormattedMessage {...messages.lblIssuedDate} />,
		<FormattedMessage {...messages.lblRetrievalInformation} />,
	];

	if (showAmendCTR) {
		data[0].push({
			id: 'amendCTRIcon',
			value: (
				<Tooltip title={<FormattedMessage {...messages.lblAmendCTR} />}>
					<button
						id="amendCTRBtn"
						key="amendCTRBtn"
						style={styles.buttonStyle}
						type="button"
						disabled={isProcessingAction}
						onClick={() => onAmendCTR(order, bookingItem)}
					>
						<img key="amendCTRIcon" alt={amendCTRText} src={ticketIcon} />
					</button>
				</Tooltip>
			),
		});
		tableHeaders.push('');
	}

	const isSTK =
		order.BookingOrderTDOData?.BookingFulfillmentInfo
			?.BookingOrderTicketDelivery === STK_TICKET_DELIVERY;

	const stkData =
		order.BookingOrderTDOData?.BookingFulfillmentInfo
			?.BookingOrderTicketDeliverySTK;

	const isMVD =
		order.BookingOrderTDOData?.BookingFulfillmentInfo
			?.BookingOrderTicketDelivery === MVD_TICKET_DELIVERY;

	const mvdTicketDeliveryData = isMVD
		? order.BookingOrderTDOData?.BookingFulfillmentInfo
				?.BookingOrderTicketDeliveryMVD || null
		: null;

	const showReturnCoupons = order?.BookBookingOrderCoupons
		? order.BookingOrderCoupons?.some(
				(c) => c.BookingCouponsIsReleasable || c.isReleasable,
			) && returnCoupons
		: returnCoupons;

	return (
		<WrapperDiv>
			<ButtonsGrid container alignItems="center">
				<TitleH2>
					<FormattedMessage {...messages.lblTitle} />
				</TitleH2>
				{displayResendTicket && (
					<Grid
						item
						xs={12}
						sm={6}
						md={4}
						lg={3}
						className="remove-padding-top"
					>
						<Button
							variant="contained"
							id={`srtBookingResendTicket_${orderId}`}
							fullWidth
							onClick={() => {
								gaEvent('resendTicketOpen');
								handleOpenResendTicketDialog(orderId);
							}}
						>
							<FormattedMessage
								{...orderDetailsMessages[
									`btn${
										order.BookingOrderStatus !== orderStatuses.CONFIRMED ||
										order.BookingOrderTicketDelivery === 'EML'
											? 'Res'
											: 'S'
									}endTicket`
								]}
							/>
						</Button>
					</Grid>
				)}
				{tdoButtonLabel && (
					<Grid
						item
						xs={12}
						sm={6}
						md={4}
						lg={3}
						className="remove-padding-top"
					>
						<Button
							variant="contained"
							id={`srtBookingAddTicketDelivery_${orderId}`}
							color="primary"
							fullWidth
							onClick={() => {
								gaEvent(`${tdoButtonLabel.event}TDOOpen`);
								handleOpenTicketDelivery(order);
							}}
						>
							<FormattedMessage {...tdoButtonLabel.message} />
						</Button>
					</Grid>
				)}
				{order.IsAllowAnyCardCollect && (
					<Grid item xs={12} sm={6} md={4} lg={3}>
						<Button
							variant="contained"
							id={`srtAllowAnyCardCollect_${orderId}`}
							data-testid="srtAllowAnyCardCollect"
							color="primary"
							fullWidth
							disabled={disableAllowAnyCardCollectButton.get(orderId)}
							onClick={() => {
								handleAllowAnyCardCollect(orderId);
							}}
						>
							<FormattedMessage
								{...orderDetailsMessages.btnAllowAnyCardCollect}
							/>
						</Button>
					</Grid>
				)}
			</ButtonsGrid>
			<TableView
				id="fulfillmentInformation"
				tableHeaders={tableHeaders}
				data={List(data)}
				showFilter={false}
				style={{ rowItemStyle: styles.rowItemStyle }}
				wrapperHeight="auto"
			/>
			{currTDO.MarketingCarrierReferences && (
				<Collapse in={isTicketingDetailsExpanded}>
					<TicketDeliveryDetails details={currTDO.MarketingCarrierReferences} />
				</Collapse>
			)}
			{isSTK && (
				<STicketActions
					orderId={orderId}
					isProcessingAction={isProcessingAction}
					sTicketStatus={sTicketStatus}
					handleSTicketAction={handleSTicketAction}
					handleSTicketDialogClose={handleSTicketDialogClose}
					isSTKSuspended={stkData?.BookingOrderTicketDeliverySTKIsSuspended}
					isSTKCanSuspend={stkData?.BookingOrderTicketDeliverySTKCanSuspend}
					isSTKCanReactivate={
						stkData?.BookingOrderTicketDeliverySTKCanReactivate
					}
					isSTKCanResetDeviceId={
						stkData?.BookingOrderTicketDeliverySTKCanResetDeviceId
					}
				/>
			)}
			<ErrorBoundary
				fallbackRender={(props) => (
					<ErrorFallBack
						{...props}
						messageComponent={
							<FormattedMessage {...messages.lblValueDocumentCouponsTitle} />
						}
					/>
				)}
			>
				<ValueDocumentCoupons
					coupons={order.BookingOrderCoupons}
					orderId={orderId}
					returnCoupons={showReturnCoupons}
					authorizeRefund={authorizeRefund}
					handleOpenReturnCouponsDialog={handleOpenReturnCouponsDialog}
					handleOpenAuthorizeRefundDialog={handleOpenAuthorizeRefundDialog}
					onMvdCouponSelect={handleMvdCouponSelect}
					mvdSelectedCoupons={mvdSelectedCoupons}
					mvdTicketStatus={mvdTicketStatus}
					handleMvdTicketAction={handleMvdTicketAction}
					handleMvdTicketDialogClose={handleMvdTicketDialogClose}
					mvdTicketDeliveryData={mvdTicketDeliveryData}
					mvdFailedTickets={mvdFailedTickets}
				/>
			</ErrorBoundary>
			{isProcessingAction && (
				<SpinnerDiv>
					<CircularProgress />
				</SpinnerDiv>
			)}
		</WrapperDiv>
	);
};

FulfillmentInformation.propTypes = {
	currTDO: PropTypes.object.isRequired,
	handleOpenStationInfoDialog: PropTypes.func.isRequired,
	order: PropTypes.object.isRequired,
	disableAllowAnyCardCollectButton: PropTypes.object.isRequired,
	showAmendCTR: PropTypes.bool.isRequired,
	onAmendCTR: PropTypes.func.isRequired,
	bookingItem: PropTypes.object.isRequired,
	tdoButtonLabel: PropTypes.object,
	amendCTRText: PropTypes.string,
	isProcessingAction: PropTypes.bool.isRequired,
	sTicketStatus: PropTypes.string,
	handleSTicketAction: PropTypes.func,
	handleSTicketDialogClose: PropTypes.func,
	returnCoupons: PropTypes.bool.isRequired,
	authorizeRefund: PropTypes.bool.isRequired,
	handleOpenReturnCouponsDialog: PropTypes.func.isRequired,
	handleOpenAuthorizeRefundDialog: PropTypes.func.isRequired,
	handleOpenResendTicketDialog: PropTypes.func.isRequired,
	handleOpenTicketDelivery: PropTypes.func.isRequired,
	handleAllowAnyCardCollect: PropTypes.func.isRequired,
	customerNumber: PropTypes.string,
	onMvdActionSuccess: PropTypes.func.isRequired,
};

export { FulfillmentInformation as FulfillmentInformationAlias };

export default FulfillmentInformation;
